import type {
  DeckingItem,
  HardwareListItem,
  InstallEquipmentLine,
  InstallLaborLine,
  MaterialsNodeData,
  PostBasesItem,
  ProposalNodeSet,
  SpeciesTimberConfig,
  TimberListItem,
} from '../types';
import { syncHardwareRollups } from './proposalHardwareCsvParser';

export type CopyableNodeKind = 'design' | 'materials' | 'timber' | 'install' | 'travel' | 'freight';

function newCopyId(prefix: string): string {
  return typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function'
    ? `${prefix}-${crypto.randomUUID()}`
    : `${prefix}-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`;
}

function cloneJson<T>(value: T): T {
  return JSON.parse(JSON.stringify(value)) as T;
}

function cloneHardwareList(list: HardwareListItem[] | undefined): HardwareListItem[] {
  return (list || []).map((row) => ({
    ...cloneJson(row),
    id: newCopyId('hw'),
  }));
}

function cloneDeckingItems(list: DeckingItem[] | undefined): DeckingItem[] | undefined {
  if (!list) return undefined;
  return list.map((row) => ({
    ...cloneJson(row),
    id: newCopyId('decking'),
  }));
}

function clonePostBasesItems(list: PostBasesItem[] | undefined): PostBasesItem[] | undefined {
  if (!list) return undefined;
  return list.map((row) => ({
    ...cloneJson(row),
    id: newCopyId('postbase'),
  }));
}

function cloneEquipmentLines(list: InstallEquipmentLine[] | undefined): InstallEquipmentLine[] {
  return (list || []).map((row) => ({
    ...cloneJson(row),
    id: newCopyId('eq'),
  }));
}

function cloneLaborLines(list: InstallLaborLine[] | undefined): InstallLaborLine[] | undefined {
  if (!list) return undefined;
  return list.map((row) => ({
    ...cloneJson(row),
    id: newCopyId('labor'),
  }));
}

function cloneTimberList(list: TimberListItem[] | undefined): TimberListItem[] {
  return (list || []).map((row) => ({
    ...cloneJson(row),
    id: newCopyId('timber'),
  }));
}

function cloneAlternateSpecies(list: SpeciesTimberConfig[] | undefined): SpeciesTimberConfig[] | undefined {
  if (!list?.length) return list;
  return list.map((alt) => ({
    ...cloneJson(alt),
    timberList: cloneTimberList(alt.timberList),
  }));
}

function sumTimberTotal(list: TimberListItem[]): number {
  return list.reduce((sum, item) => sum + (Number(item.total) || 0), 0);
}

/** Copy timber list + multi-species options; leaves hardware/decking/post bases on target. */
export function applyTimberMaterialsFromSource(
  materials: MaterialsNodeData,
  source: MaterialsNodeData,
): MaterialsNodeData {
  const timberList = cloneTimberList(source.timberList);
  const timberTotal = sumTimberTotal(timberList);
  const alternateSpecies = cloneAlternateSpecies(source.alternateSpecies);
  const altBoardFeet = (alternateSpecies || []).reduce(
    (sum, alt) => sum + (Number(alt.totalBoardFeet) || 0),
    0,
  );
  const totalBoardFeet =
    timberList.reduce((sum, item) => sum + (Number(item.boardFeet) || 0) * (Number(item.quantity) || 0), 0) +
    altBoardFeet;
  return {
    ...materials,
    timberList,
    timberTotal,
    quoteMultipleSpecies: source.quoteMultipleSpecies,
    alternateSpecies,
    totalBoardFeet,
  };
}

/** One-time copy of a single cost node. Does not keep the structures linked. */
export function copyNodeFromStructure(
  target: ProposalNodeSet,
  source: ProposalNodeSet,
  node: CopyableNodeKind,
): ProposalNodeSet {
  if (node === 'design') {
    return { ...target, design: cloneJson(source.design) };
  }

  if (node === 'install') {
    const install = cloneJson(source.install);
    install.equipmentLines = cloneEquipmentLines(source.install.equipmentLines);
    install.laborLines = cloneLaborLines(source.install.laborLines);
    return { ...target, install };
  }

  if (node === 'travel') {
    return { ...target, travel: cloneJson(source.travel) };
  }

  if (node === 'freight') {
    return { ...target, freight: cloneJson(source.freight) };
  }

  if (node === 'timber') {
    const materials = syncHardwareRollups(
      applyTimberMaterialsFromSource(target.materials, source.materials),
    );
    return { ...target, materials };
  }

  const hardwareList = cloneHardwareList(source.materials.hardwareList);
  const deckingItems = cloneDeckingItems(source.materials.deckingItems);
  const postBasesItems = clonePostBasesItems(source.materials.postBasesItems);
  const materials = syncHardwareRollups(
    applyTimberMaterialsFromSource(
      {
        ...target.materials,
        hardwareList,
        pegsCost: source.materials.pegsCost,
        fastenerPackageCost: source.materials.fastenerPackageCost,
        postBasesItems,
        postBasesProductName: source.materials.postBasesProductName,
        postBasesQty: source.materials.postBasesQty,
        postBasesUnitCost: source.materials.postBasesUnitCost,
        postBasesTotal: source.materials.postBasesTotal,
        deckingItems,
        deckingSqFt: source.materials.deckingSqFt,
        deckingWidthInches: source.materials.deckingWidthInches,
        deckingPricePerLinFt: source.materials.deckingPricePerLinFt,
        deckingTotal: source.materials.deckingTotal,
        deckingProductName: source.materials.deckingProductName,
      },
      source.materials,
    ),
  );

  return { ...target, materials };
}
